/**
 * PaywallModal — reusable paywall overlay for an empty wallet and in-game cutoff.
 *
 * Triggered by:
 * - INSUFFICIENT_COINS (HTTP 403 when starting a game)
 * - billing:paywall_required (WS event mid-game)
 *
 * Props:
 * - open: boolean — controls visibility
 * - onClose: () => void — called when user dismisses
 * - trigger: 'insufficient_coins' | 'in_game_cutoff' — adjusts copy
 * - plans: PlanOption[] — dynamic, from API/WS payload
 * - message: string — context message from backend
 */

import { useState } from "react"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "@/components/ui/dialog"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Label } from "@/components/ui/label"
import { purchasePlan } from "@/services/api"

export interface PlanOption {
    /** Plan code (e.g. "weekend_pass"), not the database UUID — it is sent as `plan_code` on purchase. */
    id: string
    name: string
    price_pln: number
    coins: number
    days: number
}

interface PaywallModalProps {
    open: boolean
    onClose: () => void
    trigger: "insufficient_coins" | "in_game_cutoff"
    plans: PlanOption[]
    message: string
}

export function PaywallModal({
    open,
    onClose,
    trigger,
    plans,
    message,
}: PaywallModalProps) {
    const { t } = useTranslation()
    const [loadingPlanId, setLoadingPlanId] = useState<string | null>(null)
    // Consumer's request to start performance before the withdrawal period ends.
    // Server-side validation is the binding one; this only keeps the UI honest.
    const [requestImmediatePerformance, setRequestImmediatePerformance] = useState(false)

    const handlePurchase = async (planId: string) => {
        setLoadingPlanId(planId)
        try {
            const { checkout_url } = await purchasePlan(planId, requestImmediatePerformance)
            window.location.assign(checkout_url)
        } catch {
            setLoadingPlanId(null)
            toast.error(t("billing.paymentError"), {
                duration: 5000,
            })
        }
    }

    return (
        <Dialog open={open} onOpenChange={(isOpen: boolean) => { if (!isOpen) onClose() }}>
            <DialogContent className="bg-background border-border max-w-md sm:max-w-lg">
                <DialogHeader>
                    <DialogTitle className="text-primary text-center text-xl font-display uppercase tracking-wider">
                        {trigger === "insufficient_coins"
                            ? t("billing.insufficientCoins")
                            : t("billing.unlockEnd")}
                    </DialogTitle>
                    <DialogDescription className="text-foreground text-center pt-1">
                        {message}
                    </DialogDescription>
                </DialogHeader>

                {/* Immediate performance request (loss of withdrawal right) */}
                <div className="flex items-start gap-2 pt-1">
                    <Checkbox
                        id="request-immediate-performance"
                        data-testid="request-immediate-performance"
                        checked={requestImmediatePerformance}
                        onChange={(e) => setRequestImmediatePerformance(e.target.checked)}
                        disabled={loadingPlanId !== null}
                        className="mt-1"
                    />
                    <Label
                        htmlFor="request-immediate-performance"
                        className="text-xs font-normal leading-snug text-muted-foreground"
                    >
                        {t("billing.immediatePerformanceConsent")}{" "}
                        <a
                            href="/regulamin"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="underline"
                            // Inside a <label>, a click would toggle the checkbox.
                            onClick={(e) => e.stopPropagation()}
                        >
                            {t("billing.immediatePerformanceTermsLink")}
                        </a>
                    </Label>
                </div>

                {/* Plan cards */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
                    {plans.map((plan) => (
                        <Card
                            key={plan.id}
                            className="bg-card border-border p-4 flex flex-col gap-3"
                        >
                            <div className="text-center">
                                <p className="text-primary font-bold uppercase tracking-widest text-sm">
                                    {plan.name}
                                </p>
                                <p className="text-foreground text-2xl font-black mt-1">
                                    {plan.price_pln} PLN
                                </p>
                            </div>
                            <ul className="text-muted-foreground text-xs space-y-1 text-center">
                                <li>{t("billing.planCoins", { count: plan.coins })}</li>
                                <li>{t("billing.planDays", { count: plan.days })}</li>
                            </ul>
                            <Button
                                className="w-full mt-auto bg-primary text-primary-foreground hover:bg-primary/90"
                                disabled={loadingPlanId !== null || !requestImmediatePerformance}
                                onClick={() => handlePurchase(plan.id)}
                            >
                                {loadingPlanId === plan.id ? (
                                    <span className="flex items-center gap-2">
                                        <svg
                                            className="animate-spin h-4 w-4"
                                            xmlns="http://www.w3.org/2000/svg"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                        >
                                            <circle
                                                className="opacity-25"
                                                cx="12"
                                                cy="12"
                                                r="10"
                                                stroke="currentColor"
                                                strokeWidth="4"
                                            />
                                            <path
                                                className="opacity-75"
                                                fill="currentColor"
                                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                            />
                                        </svg>
                                        {t('common.processing')}
                                    </span>
                                ) : (
                                    t('game.buyPlan', { planName: plan.name })
                                )}
                            </Button>
                        </Card>
                    ))}
                </div>

                <DialogFooter className="pt-2">
                    <Button
                        variant="outline"
                        onClick={onClose}
                        disabled={loadingPlanId !== null}
                        className="w-full border-border text-muted-foreground hover:bg-accent"
                    >
                        {t("billing.notNow")}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
